/**
 * The "Connect a feedback agent" page's calls (`/api/v2/internal/feedback-agents/link`, restServer
 * `apiv2/internal/feedback_agents.py`, ADR 0041): read the sign-in a developer's agent is asking
 * for, then allow or deny it. Internal: the web app's own, answered only for this browser's
 * registered web client device and only for RunDiffusion staff (see `internalApi.ts`).
 *
 * Pure, so it stays Jest-testable: the page passes the credentials (`internalApiCredentials()`).
 */

import { InternalApiCredentials, internalApiHeaders, internalApiPath } from '@/utils/internalApi';

export const FEEDBACK_AGENTS_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

// A user code as the CLI prints it: two blocks of four.
export const AGENT_CODE_PATTERN: RegExp = /^[0-9A-Z]{4}-[0-9A-Z]{4}$/;

export interface AgentLinkRequest {
	userCode: string;
	agent: string; // "Claude Code"
	host: string; // the computer it runs on
	requestedAt: Date | null;
}

export interface AgentLinkFailure {
	ok: false;
	status: number;
	code: string; // the API's error code, or NETWORK
	message: string;
}

export type AgentLinkResult<T> = { ok: true, value: T } | AgentLinkFailure;

/** The failure, or null when it worked (`web` compiles without strictNullChecks, so `!result.ok` doesn't narrow). */
export function agentLinkFailure<T> (result: AgentLinkResult<T>): AgentLinkFailure | null {
	return result.ok === true ? null : result;
}

/** What someone typed or the link carried, as the code the CLI printed: "abcd efgh" is "ABCD-EFGH". */
export function normalizeAgentCode (raw: string | null | undefined): string {
	const compact: string = (raw || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
	return compact.length === 8 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

interface FetchInput extends InternalApiCredentials {
	baseUrl: string;
	fetchImpl?: typeof fetch;
}

async function failureFrom (response: Response): Promise<AgentLinkFailure> {
	let body: { error?: { code?: string, message?: string } } | null = null;
	try {
		body = await response.json();
	} catch (e) {
		// not JSON
	}
	return {
		ok: false,
		status: response.status,
		code: body?.error?.code || `HTTP_${response.status}`,
		message: body?.error?.message || 'Something went wrong. Try again.',
	};
}

const networkFailure = (): AgentLinkFailure => ({ ok: false, status: 0, code: 'NETWORK', message: 'Couldn\'t reach RunDiffusion. Check your connection and try again.' });

export async function fetchAgentLink (input: FetchInput & { userCode: string }): Promise<AgentLinkResult<AgentLinkRequest>> {
	const url = `${input.baseUrl}${internalApiPath('/feedback-agents/link')}?code=${encodeURIComponent(input.userCode)}`;
	try {
		const response = await (input.fetchImpl || fetch)(url, { headers: internalApiHeaders(input) });
		if (!response.ok) return failureFrom(response);
		const body = await response.json();
		return {
			ok: true,
			value: {
				userCode: String(body?.user_code || input.userCode),
				agent: String(body?.agent || 'An agent'),
				host: String(body?.host || 'a computer'),
				requestedAt: body?.requested_at ? new Date(body.requested_at) : null,
			},
		};
	} catch (e) {
		return networkFailure();
	}
}

export async function decideAgentLink (input: FetchInput & { userCode: string, decision: 'allow' | 'deny' }): Promise<AgentLinkResult<null>> {
	try {
		const response = await (input.fetchImpl || fetch)(`${input.baseUrl}${internalApiPath('/feedback-agents/link')}`, {
			method: 'POST',
			headers: { ...internalApiHeaders(input), 'Content-Type': 'application/json' },
			body: JSON.stringify({ user_code: input.userCode, decision: input.decision }),
		});
		if (!response.ok) return failureFrom(response);
		return { ok: true, value: null };
	} catch (e) {
		return networkFailure();
	}
}
